import { useQuery, useApolloClient, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import EmptyState from 'components/v2/EmptyState'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader, FiltersContainer } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FaresFilters } from 'features/FareFamilies/views/Index/FaresFilters'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { GET_FARE_FAMILIES_WITH_PAGINATION } from 'features/FareFamilies/queries-legacy'
import { resolvers as faresResolvers } from 'resolvers/fares'
import { resolvers as loginResolvers } from 'resolvers/login'
import { resolvers as segmentsResolvers } from 'resolvers/segments'
import { useLogin } from 'features/Login/hooks'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'
import { FaresList } from './FaresList'
import { FareFamiliesFilters } from './Filters'
import { FareFamiliesList } from './List'
import { IFaresFilters } from 'features/FareFamilies/views/Index/FaresFilters/types'
import { EStatus } from 'features/FareFamilies/components/Status/constants'
import { useCity } from 'features/Cities/hooks'
import { FareFamiliesPagination } from 'features/FareFamilies/views/Index/Pagination'
import { GTFareFamiliesResponse, GTSegmentSuggestionsResponse } from 'gql/types.generated'
import { useCompanyAutocomplete } from 'features/Companies/hooks'
import { GET_SEGMENTS_SUGGESTIONS } from 'features/Segments/queries-legacy'

const LIMIT = 50
const TablesWrapper = styled.div<TSFixMe>`
  display: flex;
  margin: 0 -10px;

  > div {
    width: 0;
    flex: 1 0 auto;
    margin: 0 10px;
  }
`

const CardContent = styled(Card.Content)`
  max-height: 55vh;
  overflow-y: scroll;
  overflow-x: auto;
`

const FaresListCardHeader = styled(Card.Header)`
  background-color: #916ee8;
`

const messages = defineMessages({
  helmet: {
    id: 'fareFamilies.helmet',
    defaultMessage: 'Fare Families | Nexbus Admin'
  }
})

const GET_BOOKING_CLASSES = gql`
  query GetBookingClasses {
    bookingClasses {
      id
      name
      promo
      isActive
      company {
        id
        name
      }
    }
  }
`

const FareFamiliesView = (): TSFixMe => {
  const location = useLocation()
  let content
  const {
    companyId = '',
    segmentId = '',
    originCityId = '',
    destinationCityId = '',
    page = '1'
  } = getQueries(location.search)
  const pageNb = parseInt(page, 10)
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)
  client.addResolvers(segmentsResolvers)
  client.addResolvers(faresResolvers)

  const login = useLogin()
  const [activeFamilyIndex, setActiveFamilyIndex] = useState(0)
  const [faresFilters, setFaresFilters] = useState({
    status: ''
  })
  const { data: bookingClassesData, loading: bookingClassesLoading } = useQuery(GET_BOOKING_CLASSES)
  const { loading, error, data, refetch, networkStatus } = useQuery<{
    fareFamiliesWithPagination: GTFareFamiliesResponse
  }>(GET_FARE_FAMILIES_WITH_PAGINATION, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        page: pageNb,
        size: LIMIT
      },
      input: filterEmptyValue({
        companyID: companyId,
        segmentID: segmentId,
        originCityID: originCityId,
        destinationCityID: destinationCityId
      })
    },
    onCompleted() {
      setActiveFamilyIndex(0)
    }
  })
  const companyData = useCompanyAutocomplete(companyId, { skip: !companyId })
  const { data: segmentData } = useQuery<{ segmentSuggestions: GTSegmentSuggestionsResponse }>(
    GET_SEGMENTS_SUGGESTIONS,
    {
      skip: !segmentId,
      variables: {
        input: {
          id: segmentId
        }
      }
    }
  )
  const originCity = useCity(originCityId, { skip: !originCityId })
  const destinationCity = useCity(destinationCityId, { skip: !destinationCityId })

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
    refetch()
  }, [])

  if (!canRead(FEATURES.FARE_FAMILY, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.FARE_FAMILY} />
  }

  const filters = {
    company: companyData || { id: '', name: '' },
    segment: segmentData?.segmentSuggestions.items?.at(0) || { id: '', name: '' },
    originCity: originCity || { id: '', name: '' },
    destinationCity: destinationCity || { id: '', name: '' }
  }

  if (networkStatus === NetworkStatus.refetch || loading || bookingClassesLoading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="fareFamilies.error.description"
            defaultMessage="There was an error while loading your fare families"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="fareFamilies.error.action" defaultMessage="Reload your fare families" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.fareFamiliesWithPagination.items) && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="fareFamilies.empty.description"
            defaultMessage="You don&#39;t have any fare family yet"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/fare-families/new" kind="primary">
            <FormattedMessage id="fareFamilies.empty.action" defaultMessage="Add your first fare family" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.fareFamiliesWithPagination.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="fareFamilies.empty.filters.description"
            defaultMessage="There was no fare families found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/fare-families" kind="primary">
            <FormattedMessage id="fareFamilies.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <FareFamiliesList
          fareFamilies={data?.fareFamiliesWithPagination.items}
          activeFamilyIndex={activeFamilyIndex}
          setActiveFamilyIndex={setActiveFamilyIndex}
        />
        <FareFamiliesPagination
          page={pageNb}
          limit={LIMIT}
          totalItems={data?.fareFamiliesWithPagination.total || 0}
          currentItemsCount={data?.fareFamiliesWithPagination.items.length || 0}
        />
      </>
    )
  }

  const fareFamily = data?.fareFamiliesWithPagination?.items?.[activeFamilyIndex]

  const handleFiltersChange = (values: IFaresFilters): void => {
    setFaresFilters(values)
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="fareFamilies.header.label" defaultMessage="Fare families list" />
        </Heading.H3>
        {canWrite(FEATURES.FARE_FAMILY, login.accessToken.policies) && (
          <Button as={NavLink} to="/fare-families/new" kind="secondary">
            <FormattedMessage id="fareFamilies.header.new" defaultMessage="+ Add fare family" />
          </Button>
        )}
      </ContentHeader>
      <FiltersContainer>
        <FareFamiliesFilters filters={filters} />
      </FiltersContainer>
      <section>
        <TablesWrapper>
          <div>
            <Card.Container>
              <Card.Header small>
                <FormattedMessage id="fareFamilies.list.title" defaultMessage="Families" />
              </Card.Header>
              <CardContent noFooter small>
                <div>{content}</div>
              </CardContent>
            </Card.Container>
          </div>
          <div>
            {!isEmpty(fareFamily) && !loading && !error && (
              <Card.Container>
                <FaresListCardHeader small secondary>
                  <FormattedMessage
                    id="fareFamilies.faresList.title"
                    defaultMessage="Fares ({originCode} - {destinationCode})"
                    values={{
                      originCode: fareFamily.segment.origin.code,
                      destinationCode: fareFamily.segment.destination.code
                    }}
                  />
                </FaresListCardHeader>
                <Card.Content noFooter small>
                  <FaresFilters filters={faresFilters} onFiltersChange={handleFiltersChange} />
                  <FaresList
                    fareFamily={fareFamily}
                    fares={fareFamily.fares.filter((fare) => {
                      const { status } = faresFilters
                      if (status === '') return true
                      return fare.isActive === (status === EStatus.Active)
                    })}
                    bookingClasses={
                      bookingClassesData &&
                      bookingClassesData.bookingClasses &&
                      bookingClassesData.bookingClasses.filter(
                        ({ company }: TSFixMe): TSFixMe => company.id === fareFamily.company.id
                      )
                    }
                  />
                </Card.Content>
              </Card.Container>
            )}
          </div>
        </TablesWrapper>
      </section>
    </Content>
  )
}

export default withLastUrl(FareFamiliesView)
